// Don't apply 'HeadingBuilder' for this component's 'Tippy content' ('submit' field)
import React, { useContext, useState, useRef, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import * as Constants from '../constants';
import { SelectOption } from "./SelectOption";
import { ShopperContext } from '../context/shopper';
import HeadingBuilder from '../components/HeadingBuilder';
import ShowAlert from '../components/ShowAlert';
import PlanChooseRow from '../components/PlanChooseRow';
import EmailNoticeRow from '../components/EmailNoticeRow';
import { ErrorList } from './ErrorList';
import { BackButton } from './BackButton';
import { useForm } from "../hooks/useForm";
import { useStyles } from "../hooks/useStyles";
import { useLookups, usePhrasing } from "../hooks/useSkuUtils";
import { StripeCircleBuyButton } from "./StripeCircleBuyButton";
import CountryPicker from './CountryPicker';
import Table from 'react-bootstrap/Table';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import styles from '../css/trTdFirstChild.module.css';

const CircleForm = ({lightUpAddCircleButton}) => {
  const { signedIn, shopper, addCircle, clientReferenceId, setClientReferenceId,
          circles, errorsDoExist, dismissErrorsList, setErrorsDoExistToFalse,
          setJustGotClearToFalse
        } = useContext(ShopperContext);
  const { tableShow, tableData } = useStyles();
  const { lookupXipLength, lookup_tz_info } = useLookups();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const inputRef = useRef(null);

  useEffect(() => {
   if (signedIn) {
    inputRef.current.focus();
   }
  }, [signedIn]);

  const defaultPlan = 'weekly3us200';
  const [whichPlan, setWhichPlan] = useState(defaultPlan);

  const [showAddCircleFormAlert, setShowAddCircleFormAlert] = useState(false);
  const toggleAddCircleFormAlert = () => {
    setShowAddCircleFormAlert(! showAddCircleFormAlert);
  }

  const [showInfoAlert, setShowInfoAlert] = useState(false);
  const toggleInfoAlert = () =>  setShowInfoAlert(! showInfoAlert);
  

  // TODO: present commented-out attributes for entry in some Profile update
  const initialState = {
    identifier: '',
    label: '',
    country: 'US',
    postal_code: '',
    time_zone: "Eastern Time (US & Canada)",
    tax_rate: '',
    tax_rate_food: '',
    plan_identifier: defaultPlan,
    // show_help_video_icons: shopperObj.shopper.show_help_video_icons ? 1 : 0,
    share_sku_basic: 1,
    symbology: 'upca',
    // detail: '',
    // img_url: '',
    // city: '',
    // county: '',
    // state: '',
  };
  const { formData, handleChange, reset, editFormValues } = useForm(initialState);

  const resetCircleForm = () => {
    setClientReferenceId('');
    reset();
  }

  const hideCircleForm = () => {
    setClientReferenceId('');
    lightUpAddCircleButton();
  }

  // A RadioButton shows as 'selected' when its 'isChecked' attribute is true.
  // The only RadioButton whose 'isChecked' attribute is true will be the one
  // whose 'planName' matches the value in 'whichPlan'.
  // Clicking a RadioButton runs 'setWhichPlan' with the plan's 'planName',
  // causing 'isChecked' to be true for the clicked RadioButton.

  // Note - as an event handler, handlePickPlan only activates by clicking
  // a RadioButton. Unless that happens, the 'plan_identifier' value that was
  // assigned via initialState will be what's provided upon Submit.
  // Initializing 'whichPlan' via useState(defaultPlan) does not set the form's
  // 'plan_identifier' attribute. But, by making the initial 'whichPlan' and
  // 'initialState.plan_identifier' values the same, then the "prepicked" button
  // will correctly indicate the 'plan_identifier' value configured to submit.
  const handlePickPlan = (planName) => {
    setWhichPlan(planName);  // lights up/selects that RadioButton
    handleChange({ target: {name: 'plan_identifier', value: planName} });
  };

  const { circleTaxTippyText,
          circleFoodTaxTippyText,
          postalCodeTippyText } = usePhrasing();
  const circleTaxText     = circleTaxTippyText();
  const circleFoodTaxText = circleFoodTaxTippyText();
  const postalCodeText    = postalCodeTippyText({
    countryCode: formData.country,
    xipLength:   lookupXipLength({ countryCode: formData.country }) 
  });

  const timeZoneOptions = Constants.VALID_TIME_ZONES.map(
    timeZoneCode =>
      <SelectOption
        key      = {timeZoneCode}
        optValue = {lookup_tz_info({tz_term: timeZoneCode })}
        optLabel = {t('circle.tz_name.' + timeZoneCode)}
      />
  );

  const goToCircles = () => {
    // Let's go back to the 'circles' page.
    // "navigate('/circles/')" makes the screen right.
    navigate('/circles/', {replace: true});

    // Refresh since the database holds the state of 'paid' for the new Circle.
    // 'payment_status' is stuck at 'pending' until there is a refresh.
    navigate(0)
  }

  // We want to obtain the identifier/label of the new Circle.
  // If this were TypeScript, we'd be insuring 'clientReferenceId' was a string.
  const newCircle =
    (clientReferenceId && clientReferenceId.length > 0)
      ? circles.find(c => c.id == clientReferenceId)
      : null;

 if (signedIn) {
  const handleSubmit = (e) => {
    e.preventDefault();

    dismissErrorsList();
    setErrorsDoExistToFalse();
    setJustGotClearToFalse();
    setClientReferenceId('');

    console.log('formData for addCircle: ', formData);
    addCircle(formData);
  }

  const seemsLikeFreePlan = whichPlan.search(/free/i) > -1;
  // console.log('seemsLikeFreePlan is ', seemsLikeFreePlan);

  return (
      <article>
        <HeadingBuilder
          Size = "h4"
          headingText = {t('circleForm.formPart.headlineLabel')}
          // Make a new Shopping Circle: VID
          tippyAlertText = {t('circleForm.formPart.headlineTippyContent')}
          tippyText      = {Constants.DOT}
          videoUrl = "video/add-circle.mp4"
        />
        <form onSubmit={handleSubmit}>
<Table responsive striped bordered hover size="sm" style={tableShow}>
  <thead >
  </thead>
  <tbody className={styles.trTdFC} style={tableData} >
    <EmailNoticeRow />
    <tr>
      <td>
        <HeadingBuilder
          containerType  = 'container-input-label'
          headingText    = {t('circle.country.label')}
          // Country
          labelHtmlFor   = 'country_cf'
          tippyInfoText  = {t('circle.country.warningCantChange')}
          tippyAlertText = {t('circle.country.tippyContent', {
            signOut: t('global.label.sign_out')
          })}
          tippyText      = {Constants.DOT}
    // Pick a country from the list. If you don't see the country you want to ...
        />
      </td>
      <td>
        <CountryPicker
          countryKey      = '10'
          countryId       = 'circleCountryPicker'
          formData        = {formData}
          handleChange    = {handleChange}
          countryList     = {Constants.VALID_CIRCLE_COUNTRIES}
        />
        &nbsp;*
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('circle.postal_code.label')}
          // Zip Code
          labelHtmlFor = "postal_code_cf"
          tippyAlertText = {postalCodeText}
          tippyText = {postalCodeText}
        />
      </td>
      <td>
        <input
          key="20"
          type="text"
          id="postal_code_cf"
          name="postal_code"
          onChange={handleChange}
          value={formData.postal_code}
          required
        />
        &nbsp;*
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('circle.identifier.label')}
          // Shopping Circle ID:
          labelHtmlFor = "identifier_cf"
          tippyAlertText = {t('circle.identifier.tippyContent')}
          tippyText = {t('circle.identifier.tippyContent')}
        />
      </td>
      <td>
        <input
          key="30"
          ref={inputRef}
          type="text"
          style={{ textTransform: "lowercase" }}
          id="identifier_cf"
          name="identifier"
          placeholder={t('circle.identifier.placeholder')}
          // autoComplete="username"
          onChange={handleChange}
          value={formData.identifier}
          required
        />
        &nbsp;*
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('circle.label.label')}
          // Description
          labelHtmlFor = "label_cf"
          tippyAlertText = {t('circle.label.tippyContent')}
          tippyText = {t('circle.label.tippyContent')}
        />
      </td>
      <td>
        <input
          key="40"
          type="text"
          id="label_cf"
          name="label"
          placeholder={t('circle.identifier.label')}
          onChange={handleChange}
          value={formData.label}
        />
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType  = 'container-input-label'
          headingText    = {t('circle.time_zone.label')}
          // Time Zone
          labelHtmlFor   = "time_zone_cf"
          tippyAlertText = {t('circle.time_zone.tippyContent')}
          tippyText      = {t('circle.time_zone.tippyContent')}
          // Where will your circle operate?
        />
      </td>
      <td>
        <select
          key="45"
          type="text"
          id="time_zone_cf"
          name="time_zone"
          onChange={handleChange}
          value={formData.time_zone}
        >
          {/* <option value="">Choose one:/option> */}
          <option value='' disabled>{t('global.chooseOne')}</option>

          {timeZoneOptions}
        </select>
        &nbsp;*
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('circle.symbology.label')}
          // Barcode Length
          labelHtmlFor = "symbology"
          tippyAlertText = {t('circle.symbology.tippyContent')}
          tippyText      = {Constants.DOT}
        />
        {/* How many digits are in the barcodes of the products you buy? */}
      </td>
      <td>
        <select
          key="47"
          type="text"
          id="symbology"
          name="symbology"
          onChange={handleChange}
          value={formData.symbology}
          required
        >
          <option value="" disabled>{t('global.chooseOne')}</option>
          {/* <option value="">Choose one:</option> */}
          <option value="upca">{t('circle.symbology.optionValue0')}</option>
          {/* <option value="upca">12 digits (US and Canada)</option> */}
          <option value="ean13">{t('circle.symbology.optionValue1')}</option>
          {/* <option value="ean13">13 digits (most of the world)</option> */}
        </select>
        &nbsp;*
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType  = 'container-input-label'
          headingText    = {t('circle.share_sku_basic.label')}
            // Share basic Sku info
          labelHtmlFor   = "share_sku_basic"
          tippyAlertText = {t('circle.share_sku_basic.tippyContent', {
              shareDetails: t('sku.shareable.label'),
          })}
          tippyText      = {Constants.DOT}
          // A 'yes' value here allows ShopWeAll to copy basic information...
          tippyInfoText  = {t('circle.share_sku_basic.tippyContentInfo', {
              shareDetails: t('sku.shareable.label'),
              details: t('sku.detail.label'),
          })}
        />
      </td>
      <td>
        <select
          key="48"
          type="text"
          id="share_sku_basic"
          name="share_sku_basic"
          onChange={handleChange}
          value={formData.share_sku_basic}
        >
          {/* <option value="0">No</option> */}
          <option value="0">{t('shopper.showVideoTipsIcon.optionValue0')}</option>
          {/* <option value="1">Yes</option> */}
          <option value="1">{t('shopper.showVideoTipsIcon.optionValue1')}</option>
        </select>
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('circle.tax_rate.label')}
          // Sales Tax Rate
          labelHtmlFor = "tax_rate"
          tippyAlertText = {circleTaxText}
          tippyText      = {circleTaxText}
        />
      </td>
      <td>
        <NumericFormat displayType="input"
          key="50"
          type="text"
          id="tax_rate"
          name="tax_rate"
          onChange={handleChange}
          value={formData.tax_rate}
          decimalScale={3}
        />
        &nbsp;%
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('circle.tax_rate_food.label')}
          // Food Sales Tax Rate
          labelHtmlFor = "tax_rate_food"
          tippyAlertText = {circleFoodTaxText}
          tippyText      = {circleFoodTaxText}
        />
      </td>
      <td>
        <NumericFormat displayType="input"
          key="60"
          type="text"
          id="tax_rate_food"
          name="tax_rate_food"
          onChange={handleChange}
          value={formData.tax_rate_food}
          decimalScale={3}
        />
        &nbsp;%
      </td>
    </tr>
    <PlanChooseRow
      handlePickPlan = {handlePickPlan}
      whichPlan      = {whichPlan}
      slotsUsed      = {1}
    />
  </tbody>
</Table>
          { errorsDoExist && <ErrorList /> }
         <div className="oneline-container">
          <HeadingBuilder
            // heading is a stand-alone Alert button/circle for Submit
            tippyAlertText = {t('circle.addcircle_explainer.tippyContent')}
            // Use this form to make a new Shopping Circle.
            showTippyAlert = {showAddCircleFormAlert}
            toggleTippyAlert = {toggleAddCircleFormAlert}
          />
          <Tippy content= {t('circle.addcircle_explainer.tippyContent')} >
            <input key="200" type='submit' value='Submit' />
          </Tippy>&nbsp;
          <HeadingBuilder
            // heading is a stand-alone Info button/circle for Submit
            tippyInfoText = {t('circle.addcircle_explainer.tippyContentStripe',
          {circleOrSubscription: t('circle.addcircle_explainer.createCircle')})}
            // Clicking 'Submit' creates a Circle with 'payment_status' of 'pending'. A 'Subscribe' button soon appears...
            showInfoAlert = {showInfoAlert}
            toggleInfoAlert = {toggleInfoAlert}
          />
         </div>
         <ShowAlert showAlert = {showAddCircleFormAlert}
                     alertMsg = {t('circle.addcircle_explainer.tippyContent')} />
         <ShowAlert showAlert = {showInfoAlert}
                     alertMsg = {
        t('circle.addcircle_explainer.tippyContentStripe',
          {circleOrSubscription: t('circle.addcircle_explainer.createCircle')})
                                } />
        </form>
        <br/>
        <Table responsive>
          <thead>
          </thead>
          <tbody>
            <tr>
              <td>
            <HeadingBuilder
              buttonAction   = {resetCircleForm}
              headingText    = {t('global.label.reset')}
              // Reset
              tippyAlertText = {t('circle.addcircle_resetButton.tippyContent')}
              tippyText      = {t('circle.addcircle_resetButton.tippyContent')}
            />
              </td>
              <td>
            <HeadingBuilder
              buttonAction   = {hideCircleForm}
              headingText    = {t('global.label.hide')}
              // Hide
              tippyAlertText = {t('circle.addcircle_hideButton.tippyContent')}
              tippyText      = {t('circle.addcircle_hideButton.tippyContent')}
            />
              </td>
            </tr>
          </tbody>
        </Table>
        {
          // If handleSubmit has caused setting a value for clientReferenceId
          // then show the associated Stripe Buy Button
          clientReferenceId && clientReferenceId.length > 0

          // ".length" should filter out "free" circles, but just in case:
          &&  !seemsLikeFreePlan

          &&
         <>
          <HeadingBuilder
            headingText    = {t('circle.addcircle_explainer.headingSubscribe',
        {circleOrSubscription: t('circle.addcircle_explainer.youMadeCircle')})}
            // You created a Circle!! Now click 'Subscribe' to arrange payment.
            tippyAlertText = {t('circle.addcircle_explainer.tippyContentSubscribe')}
            tippyText      = {t('circle.addcircle_explainer.tippyContentSubscribe')}
            // Note: Clicking 'Subscribe' will open a tab that does not have a 'Cancel' button...
tippyInfoText = {t('circle.addcircle_explainer.tippyContentSubscribeInfo',
{hideSubscribeButtonSteps: t('circle.addcircle_explainer.hideCircleSubscribe')})}
          />
          <StripeCircleBuyButton
            whichPlan = {whichPlan}
          />
          <HeadingBuilder
            Size           = 'h4'
            headingText    =
                        {t('circle.addcircle_explainer.headingAfterSubscribe')}
            // *** After you Subscribe, click HERE to finish processing ***
            buttonAction   = {goToCircles}
            goButtonText   = {Constants.GET_ICON_RT_ARROW}
            tippyAlertText =
                    {t('circle.addcircle_explainer.tippyContentAfterSubscribe')}
            // The only thing left is to nudge your browser to refresh...
            tippyText      = {Constants.DOT}
          />
          <HeadingBuilder
            headingText    = {t('circle.removeCircle.buttonLabelStripe', {
          newCircleIdentifier: newCircle ? ` '${newCircle.identifier}'` : null
            })}
            // Or, you can Delete this new Circle 'oiyup'
            linkUrl        =
                  {`/circles/${clientReferenceId}/delete?clear_client_ref_id=1`}
            goButtonText   = {Constants.GET_ICON_RT_ARROW}
            tippyAlertText = {t('circle.removeCircle.tippyContentStripe')}
            tippyText      = {t('circle.removeCircle.tippyContentStripe')}
            // it would be nice to clear 'clientReferenceId' and then run
            // 'setJustGotClearToBeFalse()' here, but works to signal via query string
          />
         </>
        }
        <BackButton />
      </article>
  )

 } else {
    return (
      <HeadingBuilder
        Size = 'h3'
        headingText = {t('global.notSignedInMessage')}
        // Not Authorized - Please Sign in or Sign up
      />
    )
 }
}

export default CircleForm;
